import { FC } from 'react';
import { Skeleton, Box, Stack, SkeletonProps } from '@mui/material';

interface SkeletonLoaderProps extends SkeletonProps {
  type?: 'text' | 'rectangular' | 'rounded' | 'circular' | 'form-field' | 'dropdown' | 'table-row';
  lines?: number;
  spacing?: number;
}

export const SkeletonLoader: FC<SkeletonLoaderProps> = ({ 
  type = 'text', 
  lines = 1, 
  spacing = 1,
  ...props 
}) => {
  if (type === 'form-field') {
    return (
      <Box>
        <Skeleton variant="text" width="30%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="rounded" height={56} />
      </Box>
    );
  }

  if (type === 'dropdown') {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="rounded" height={56} />
      </Box>
    );
  }

  if (type === 'table-row') {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="text" sx={{ flex: 1 }} />
        <Skeleton variant="text" width="20%" />
        <Skeleton variant="text" width="15%" />
      </Stack>
    );
  }

  if (lines > 1) {
    return (
      <Stack spacing={spacing}>
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton key={index} variant={type} {...props} />
        ))}
      </Stack>
    );
  }

  return <Skeleton variant={type} {...props} />;
};

// Form skeleton loader for entire forms
export const FormSkeletonLoader: FC<{ fields?: number }> = ({ fields = 4 }) => (
  <Stack spacing={3}>
    {Array.from({ length: fields }).map((_, index) => (
      <SkeletonLoader key={index} type="form-field" />
    ))}
  </Stack>
);

// Table skeleton loader
export const TableSkeletonLoader: FC<{ rows?: number }> = ({ rows = 5 }) => (
  <Stack spacing={2}>
    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
      <Stack direction="row" spacing={2}>
        <Skeleton variant="text" width="10%" />
        <Skeleton variant="text" sx={{ flex: 1 }} />
        <Skeleton variant="text" width="20%" />
        <Skeleton variant="text" width="15%" />
      </Stack>
    </Box>
    {Array.from({ length: rows }).map((_, index) => (
      <Box key={index} sx={{ p: 2 }}>
        <SkeletonLoader type="table-row" />
      </Box>
    ))}
  </Stack>
);

export default SkeletonLoader;